import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, MessageSquare, FileText, DollarSign, Download, Mail } from "lucide-react";

export default function AdminDashboardTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/dashboard/stats`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setStats(data);
    } catch (error) {
      console.error("Dashboard error:", error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/export/pdf`, { headers: getAuthHeaders(), credentials: "include" });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `markeemix-report-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  if (!stats) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-dashboard-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
          <Download className="w-4 h-4 mr-2" />
          Export PDF Report
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{stats.stats.users.total}</p>
              <p className="text-sm text-green-600">{stats.stats.users.growth}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Messages</p>
              <p className="text-3xl font-bold text-gray-900">{stats.stats.contacts.total}</p>
              <p className="text-sm text-orange-600">{stats.stats.contacts.unread} unread</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-3xl font-bold text-gray-900">{"\u20B9"}{stats.stats.payments.revenue.toLocaleString()}</p>
              <p className="text-sm text-green-600">{stats.stats.payments.successful} orders</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Published Blogs</p>
              <p className="text-3xl font-bold text-gray-900">{stats.stats.blogs.published}</p>
              <p className="text-sm text-gray-500">{stats.stats.blogs.drafts} drafts</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-semibold mb-4">Tool Usage (Today)</h3>
        <div className="grid grid-cols-5 gap-4">
          {Object.entries(stats.stats.tool_usage).map(([tool, count]) => (
            <div key={tool} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-violet-600">{count}</p>
              <p className="text-xs text-gray-500 capitalize">{tool.replace("_", " ")}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Recent Users</h3>
          <div className="space-y-3">
            {stats.recent_users.map((user) => (
              <div key={user.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{user.name || user.email}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Recent Messages</h3>
          <div className="space-y-3">
            {stats.recent_contacts.map((contact) => (
              <div key={contact.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${contact.status === "unread" ? "bg-orange-100" : "bg-green-100"}`}>
                  <Mail className={`w-4 h-4 ${contact.status === "unread" ? "text-orange-600" : "text-green-600"}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{contact.name}</p>
                  <p className="text-xs text-gray-500 truncate">{contact.subject}</p>
                </div>
                {contact.status === "unread" && <Badge className="bg-orange-100 text-orange-700">New</Badge>}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
